import { format, formatDefaultLocale } from "d3-format"
import { isFinite, get, reduce, trim, isNaN, replace, isNil } from "lodash"

formatDefaultLocale({
  decimal: ".",
  thousands: ",",
  grouping: [3],
  currency: ["$", ""],
  minus: "-",
})

const BIG_NUMBER_FORMAT = format("$.3f")
const BIG_NUMBER_FORMAT_SHORT = format(".2f")
const PERCENTAGE_FORMAT = format(".2%")
const ONE_DECIMAL_PERCENTAGE_FORMAT = format(".1%")
const SMALL_PERCENTAGE_FORMAT = format(".3%")
const PERCENTAGE_NO_SYMBOL_FORMAT = value => replace(PERCENTAGE_FORMAT(value), "%", "")
const ROUND_PERCENTAGE_FORMAT = format(".0%")
const NUMBER_FORMAT = format(".2f")
const SMALL_NUMBER_FORMAT = format(".3f")
const NUMBER_COMMAS_FORMAT = format("$,.0f")
const INT_FORMAT = format(",.0f")

export const DEFAULT_CURRENCY = "USD"
export const FORMATS = {
  BIG_NUMBER_FORMAT,
  BIG_NUMBER_FORMAT_SHORT,
  PERCENTAGE_FORMAT,
  ONE_DECIMAL_PERCENTAGE_FORMAT,
  SMALL_PERCENTAGE_FORMAT,
  ROUND_PERCENTAGE_FORMAT,
  NUMBER_FORMAT,
  SMALL_NUMBER_FORMAT,
  NUMBER_COMMAS_FORMAT,
  INT_FORMAT,
}

export const numberCheck = /^-?\d+\.?\d*$/

function formatter(format, options) {
  const filler = get(options, "emptyValueFiller", "")
  return value => isFinite(value) ? format(value) : filler
}

function getFractionDigits(value) {
  return Math.abs(value) < 1000 ? 2 : 3
}

export function getCurrencySymbol(currency) {
  return (currency ?? DEFAULT_CURRENCY) === DEFAULT_CURRENCY ? "$" : currency
}

export function isDefaultCurrency(currency) {
  return !currency || currency === DEFAULT_CURRENCY
}

/**
 * Formats values using a compact format. The number of decimal places is determined by the value.
 *
 * @param {*} value The numeric value to format
 * @param {*} currency The [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code (defaults to `USD`)
 * @param {*} options
 * @param {*} options.displayCurrencySymbol - Whether or not to display the currency symbol (defaults to `true`)
 *
 * Examples:
 *
 * ```
 * formatCurrency(1510232)                                             // $1.510M
 * formatCurrency(1512632123, 'USD')                                   // $1.513B
 * formatCurrency(1512632123, 'USD', { displayCurrencySymbol: false }) // 1.513B
 * ```
 *
 * Leverages [Intl.NumberFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts)
 */
export function formatCurrency(value, currency, options) {
  if (isNaN(value) || isNil(value)) {
    return "-"
  }

  if (value === Infinity || value === -Infinity) {
    return value
  }

  const displayCurrencySymbol = get(options, "displayCurrencySymbol") !== false
  const currencySymbol = displayCurrencySymbol ? getCurrencySymbol(currency) : ""
  const fractionDigits = get(options, "fractionDigits", getFractionDigits(value))

  const formatter = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? DEFAULT_CURRENCY,
    currencyDisplay: "symbol",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    notation: "compact",
    compactDisplay: "short",
    // ensures only one "integer" part is returned
    useGrouping: false,
  })

  const reducedParts = reduce(formatter.formatToParts(value), (accParts, part) => {
    return {
      ...accParts,
      [part.type]: part.value,
    }
  }, {})

  let prefix = ""
  let suffix = ""
  if (!currency || currency === DEFAULT_CURRENCY) {
    prefix = `${currencySymbol}`
  } else {
    suffix = `${currencySymbol}`
  }

  const formatted = `${get(reducedParts, "minusSign", "")}${prefix}${reducedParts.integer}${get(reducedParts, "decimal", "")}${get(reducedParts, "fraction", "")}${get(reducedParts, "compact", "")} ${suffix}`
  return trim(formatted)
}

/**
 * Returns a function that formats values using the given currency and options. Refer to the `formatCurrency` function for more details.
 */
export const makeCurrencyFormatter = (currency, options = {}) => {
  return value => formatCurrency(value, currency, options)
}

export function formatCurrencyParens(value) {
  return value < 0 ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value)
}

export const percentageFormatter = (value, alreadyScaled) => {
  if (alreadyScaled) {
    return `${formatter(NUMBER_FORMAT)(value)}%`
  } else {
    return formatter(PERCENTAGE_FORMAT)(value)
  }
}
export const scaledPercentageFormatter = (value) => {
  return percentageFormatter(value, true)
}
export const oneDecimalPercentageFormatter = formatter(ONE_DECIMAL_PERCENTAGE_FORMAT)
export const smallPercentageFormatter = formatter(SMALL_PERCENTAGE_FORMAT)
export const percentageNoSymbolFormatter = formatter(PERCENTAGE_NO_SYMBOL_FORMAT)
export const roundPercentageFormatter = formatter(ROUND_PERCENTAGE_FORMAT)
export const numberFormatter = formatter(NUMBER_FORMAT)
export const numberCommasFormatter = formatter(NUMBER_COMMAS_FORMAT)
export const smallNumberFormatter = formatter(SMALL_NUMBER_FORMAT)
export const intFormatter = formatter(INT_FORMAT)

function customAutoFormatter(format, options) {
  return value => {
    if (!isFinite(value)) {
      return get(options, "emptyValueFiller", "-")
    }

    const absValue = Math.abs(value)
    switch (true) {
      case absValue < 1000:
        return intFormatter(value)
      case absValue < 1000000:
        return `${format(value / 1000)}K`
      case absValue < 1000000000:
        return `${format(value / 1000000)}M`
      default:
        return `${format(value / 1000000000)}B`
    }
  }
}

export const assetDollarFormatter = makeCustomFormatter(FORMATS.SMALL_NUMBER_FORMAT, { autoFormat: true })
export const autoFormatter = customAutoFormatter(BIG_NUMBER_FORMAT_SHORT)

export function makeCustomFormatter(format, options = {}) {
  if (options.autoFormat) {
    return customAutoFormatter(format, options)
  }
  return formatter(format, options)
}

export function factorFormatter(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return "-"
  } else {
    return numberFormatter(value)
  }
}

export function makeCustomFactorFormatter(isCurrency) {
  if (isCurrency) {
    return assetDollarFormatter
  }
  return factorFormatter
}

export const makeDecimalPercentageFormatter = (nbDecimals) => formatter(format(`.${nbDecimals}%`))
